import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  CheckCircle,
} from "lucide-react";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const formRef = useRef();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .sendForm(
        "service_v1ui67x", // replace with your EmailJS service ID
        "template_9bzr6fd", // replace with your EmailJS template ID
        formRef.current,
        "jzz5LjP1wmDfDOqvu" // replace with your EmailJS public key
      )
      .then(
        () => {
          setLoading(false);
          setShowSuccessPopup(true);
          setFormData({ name: "", email: "", phone: "", message: "" });
        },
        (error) => {
          setLoading(false);
          alert("Failed to send message. Please try again later.");
          console.error("EmailJS Error:", error);
        }
      );
  };


  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      info: "+91 79931 49818",
      subInfo: "Mon-Sat, 9AM-8PM",
    },
    {
      icon: Mail,
      title: "Email",
      info: "satish@pvrcabs.com",
      subInfo: "We reply within 24 hours",
    },
    {
      icon: MapPin,
      title: "Udaya Vensar Jubilee garden Flat no-601 Kondapur 500084",
      info: "Hyderabad, Telangana",
      subInfo: "India",
    },
  ];

  const socialLinks = [
    { icon: Facebook, name: "Facebook", color: "blue" },
    { icon: Twitter, name: "Twitter", color: "sky" },
    { icon: Instagram, name: "Instagram", color: "pink" },
    { icon: Linkedin, name: "LinkedIn", color: "blue" },
  ];

  return (
    <div className="h-full lg:min-h-screen bg-blue-950 py-4 md:py-12 px-3 md:px-10 mt-8">
      {/* Section Header */}
      <div className="bg-white p-6 rounded-md shadow-sm text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Get in <span className="text-green-600">Touch</span>
        </h1>
        <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
          Have questions about our services? We're here to help. Contact us
          today!
        </p>
      </div>

      {/* Main Grid */}
      <div className="max-w-7xl mx-auto mt-4 md:mt-8 grid lg:grid-cols-3 gap-4 md:gap-8">
        {/* Left: Contact Info & Social Links */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Contact Information
            </h3>
            <div className="space-y-4">
              {contactInfo.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <item.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-900">{item.info}</p>
                    <p className="text-xs text-gray-600">{item.subInfo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Follow Us
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {socialLinks.map((social, index) => (
                <button
                  key={index}
                  className={`flex items-center space-x-2 p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${social.color === "blue"
                    ? "border-blue-200 hover:bg-blue-50"
                    : social.color === "sky"
                      ? "border-sky-200 hover:bg-sky-50"
                      : "border-pink-200 hover:bg-pink-50"
                    }`}
                >
                  <social.icon
                    className={`w-5 h-5 ${social.color === "blue"
                      ? "text-blue-600"
                      : social.color === "sky"
                        ? "text-sky-600"
                        : "text-pink-600"
                      }`}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {social.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-xl font-semibold text-blue-900 mb-2">
              Send us a Message
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              We'll get back to you as soon as possible
            </p>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your mobile number"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Enter your message"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-[9999]">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Message Sent Successfully!
              </h3>
              <p className="text-gray-600 mb-4">
                Thank you for contacting us. We’ll get back to you shortly.
              </p>
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mt-8">
        <div className="relative h-64 md:h-80 lg:h-96">
          <iframe
            className="w-full h-full rounded-lg shadow-md"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.9449657542514!2d78.3695722!3d17.462348!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93ceea49d8d9%3A0x23e7ee8e40d707ae!2sUdaya%20Vensar%20Apartments!5e0!3m2!1sen!2sin!4v1751885132802!5m2!1sen!2sin"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </div>
  );
}
